import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Users, Shield, CreditCard, FileText, Clock, CheckCircle, X, 
  ExternalLink, RefreshCw, Eye, Wallet, AlertTriangle, Link2, Search,
  Upload, FileX, File, Car, Calendar, DollarSign, Phone, Mail, Copy,
  Trash2, Settings
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Enhanced User Details Drawer with multiple tabs:
 * TAB 1: Overview - Account Type, Host Status, Verification Status
 * TAB 2: Verification & Documents - S3 document viewer with status badges
 * TAB 3: Payout Status - Payout details with admin access
 * TAB 4: Bookings - User's bookings as host/customer
 * TAB 5: Audit Log - User's activity history
 */
const UserDetailsDrawer = ({ 
  isOpen, 
  onClose, 
  onRefresh,
  user: selectedUser, 
  verification: selectedVerification,
  api,
  onApproveVerification,
  onRejectVerification,
  onOpenDocument,
  actionLoading,
  onStartShadowSession,
  onDeleteUser  // NEW: Callback to open delete modal
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stripeData, setStripeData] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeLoginLoading, setStripeLoginLoading] = useState(false);
  const [stripeMode, setStripeMode] = useState('TEST'); // Default to TEST
  const [reconcileData, setReconcileData] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingInspector, setShowBookingInspector] = useState(false);
  const [docLoading, setDocLoading] = useState({});
  const [docStatus, setDocStatus] = useState({});
  const [docUrls, setDocUrls] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [licenceExpiryDate, setLicenceExpiryDate] = useState('');
  const [licenceExpiryUpdating, setLicenceExpiryUpdating] = useState(false);
  const [featureSaving, setFeatureSaving] = useState(false);
  const [localEntitlements, setLocalEntitlements] = useState(null);

  // Fetch Stripe environment mode on mount
  useEffect(() => {
    const fetchStripeEnv = async () => {
      try {
        const response = await api.get('/stripe/environment');
        setStripeMode(response.data?.mode || 'TEST');
      } catch (error) {
        console.error('Error fetching Stripe environment:', error);
      }
    };
    if (api) fetchStripeEnv();
  }, [api]);

  // Check if user is platform owner (verification not required)
  const isPlatformOwner = selectedUser?.roles?.some(r => {
    const roleName = typeof r === 'string' ? r : r?.role;
    return roleName === 'platform_owner';
  });

  // Check if user has protected roles (cannot be deleted)
  const isProtectedUser = () => {
    if (!selectedUser?.roles) return false;
    const protectedRoles = ['platform_owner', 'admin', 'super_admin'];
    return selectedUser.roles.some(r => {
      const roleName = typeof r === 'string' ? r : r?.role;
      return protectedRoles.includes(roleName) && (typeof r === 'string' || r?.is_active !== false);
    });
  };

  // Determine account type from roles
  const getAccountType = () => {
    if (!selectedUser?.roles || selectedUser.roles.length === 0) return 'user';
    const roleNames = selectedUser.roles.map(r => typeof r === 'string' ? r : r?.role).filter(Boolean);
    if (roleNames.includes('platform_owner')) return 'Platform Owner';
    if (roleNames.includes('super_admin')) return 'Super Admin';
    if (roleNames.includes('admin')) return 'Admin';
    return 'User';
  };

  // Reset state when user changes
  useEffect(() => {
    if (selectedUser?.user_id) {
      setStripeData(null);
      setReconcileData(null);
      setBookings([]);
      setAuditLogs([]);
      setDocStatus({});
      setDocUrls({});
      setLocalEntitlements({
        subscription_tier: selectedUser.subscription_tier || 'free',
        feature_flags: {
          gps_tracking: false, priority_support: false, boost_listing: false,
          advanced_analytics: false, ai_assistant: false, insurance_perks: false,
          ...(selectedUser.feature_flags || {})
        }
      });
    }
  }, [selectedUser?.user_id]);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'stripe' && selectedUser?.user_id) {
      fetchStripeData();
    } else if (activeTab === 'bookings' && selectedUser?.user_id) {
      fetchUserBookings();
    } else if (activeTab === 'vehicles' && selectedUser?.user_id) {
      fetchVehicles();
    } else if (activeTab === 'audit' && selectedUser?.user_id) {
      fetchAuditLogs();
    } else if (activeTab === 'verification' && selectedUser?.user_id && selectedVerification) {
      checkDocumentStatuses();
    }
  }, [activeTab, selectedUser?.user_id]);

  // Check document statuses via presign endpoint and cache URLs
  const checkDocumentStatuses = async () => {
    if (!selectedVerification) return;
    
    const docs = ['licence_front', 'licence_back', 'selfie'];
    const statuses = {};
    const urls = {};
    
    for (const docType of docs) {
      const hasKey = selectedVerification[docType];
      if (!hasKey) {
        statuses[docType] = 'not_uploaded';
      } else {
        try {
          const res = await api.get(`/admin/users/${selectedUser.user_id}/verification/presign?doc_type=${docType}`);
          if (res.data.status === 'missing') {
            statuses[docType] = 'missing';
          } else {
            statuses[docType] = 'available';
            urls[docType] = res.data.url;
          }
        } catch {
          statuses[docType] = 'error';
        }
      }
    }
    
    setDocStatus(statuses);
    setDocUrls(urls);
  };

  const updateUserFeature = async (payload) => {
    if (!selectedUser?.user_id || !api) return;
    setFeatureSaving(true);
    try {
      const res = await api.patch(`/admin/users/${selectedUser.user_id}/features`, payload);
      setLocalEntitlements({
        subscription_tier: res.data.subscription_tier,
        feature_flags: res.data.feature_flags
      });
      toast.success('Features updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update features');
    } finally {
      setFeatureSaving(false);
    }
  };

  const fetchStripeData = async () => {
    setStripeLoading(true);
    try {
      const res = await api.get(`/admin/users/${selectedUser.user_id}/stripe-status`);
      setStripeData(res.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch Stripe data:', error);
      }
      setStripeData(null);
    } finally {
      setStripeLoading(false);
    }
  };

  const handleRefreshStripeStatus = async () => {
    setStripeLoading(true);
    try {
      const res = await api.post(`/admin/users/${selectedUser.user_id}/refresh-stripe-status`);
      setStripeData(res.data);
      toast.success('Stripe status refreshed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh Stripe status');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleReconcileStripe = async () => {
    setReconcileLoading(true);
    setReconcileData(null);
    try {
      const res = await api.post(`/admin/users/${selectedUser.user_id}/stripe/reconcile`);
      setReconcileData(res.data);
      
      if (res.data.status === 'reconciled' || res.data.status === 'already_connected') {
        toast.success('Payout account found and connected!');
        await fetchStripeData();
      } else if (res.data.status === 'candidates_found') {
        toast.info(`Found ${res.data.candidates.length} candidate account(s)`);
      } else {
        toast.warning('No matching payout accounts found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconcileLoading(false);
    }
  };

  const handleAttachAccount = async (accountId) => {
    setReconcileLoading(true);
    try {
      const res = await api.post(`/admin/users/${selectedUser.user_id}/stripe/attach`, {
        account_id: accountId
      });
      toast.success('Account attached successfully!');
      setReconcileData(null);
      await fetchStripeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to attach account');
    } finally {
      setReconcileLoading(false);
    }
  };

  // Use backend-generated login link instead of direct URL
  const handleOpenStripeAdmin = async () => {
    setStripeLoginLoading(true);
    try {
      const res = await api.post(`/admin/users/${selectedUser.user_id}/stripe/login-link`);
      if (res.data.url) {
        window.open(res.data.url, '_blank');
        toast.success('Stripe admin access opened');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate Stripe login link');
    } finally {
      setStripeLoginLoading(false);
    }
  };

  const fetchUserBookings = async () => {
    setBookingsLoading(true);
    try {
      const res = await api.get(`/admin/users/${selectedUser.user_id}/bookings`);
      setBookings(res.data.bookings || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      setBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const res = await api.get(`/admin/users/${selectedUser.user_id}/audit-logs?limit=100`);
      setAuditLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchVehicles = async () => {
    setVehiclesLoading(true);
    try {
      const res = await api.get(`/admin/users/${selectedUser.user_id}/vehicles`);
      setVehicles(res.data.vehicles || []);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      setVehicles([]);
    } finally {
      setVehiclesLoading(false);
    }
  };

  const openBookingInspector = async (bookingId) => {
    try {
      const res = await api.get(`/admin/bookings/${bookingId}/payment-details`);
      setSelectedBooking(res.data);
      setShowBookingInspector(true);
    } catch (error) {
      const booking = bookings.find(b => b.booking_id === bookingId);
      setSelectedBooking(booking || { booking_id: bookingId });
      setShowBookingInspector(true);
    }
  };

  // Open verification document via presigned URL endpoint
  const handleOpenDocument = async (docType) => {
    setDocLoading(prev => ({ ...prev, [docType]: true }));
    try {
      const res = await api.get(`/admin/users/${selectedUser.user_id}/verification/presign?doc_type=${docType}`);
      
      if (res.data.status === 'missing') {
        toast.error(`Document missing in storage: ${docType}`);
        return;
      }
      
      if (res.data.url) {
        window.open(res.data.url, '_blank');
        toast.success('Document opened');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open document');
    } finally {
      setDocLoading(prev => ({ ...prev, [docType]: false }));
    }
  };

  // Get document status badge
  const getDocStatusBadge = (docType) => {
    const status = docStatus[docType];
    if (status === 'not_uploaded') {
      return <Badge className="text-[10px] bg-slate-500/20 text-slate-400">Not uploaded</Badge>;
    }
    if (status === 'missing') {
      return <Badge className="text-[10px] bg-red-500/20 text-red-400">Missing in storage</Badge>;
    }
    if (status === 'available') {
      return <Badge className="text-[10px] bg-green-500/20 text-green-400">Available</Badge>;
    }
    if (status === 'error') {
      return <Badge className="text-[10px] bg-red-500/20 text-red-400">Unable to load</Badge>;
    }
    return <Badge className="text-[10px] bg-white/10 text-white/40">Loading...</Badge>;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Users },
    { id: 'verification', label: 'Verification', icon: Shield },
    { id: 'stripe', label: 'Payouts', icon: CreditCard },
    { id: 'vehicles', label: 'Vehicles', icon: Car },
    { id: 'bookings', label: 'Bookings', icon: FileText },
    { id: 'audit', label: 'Audit Log', icon: Clock },
    { id: 'contact', label: 'Contact', icon: Phone }
  ];

  if (!selectedUser) return null;

  // Get host_status
  const hostStatus = selectedUser.host_status || 'none';
  const accountType = getAccountType();

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-slate-900 border-white/20 text-white mx-4 max-w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          {/* Fixed Header */}
          <div className="flex-shrink-0 px-6 pt-6 pb-2 border-b border-white/10 bg-slate-900">
            <DialogHeader className="mb-0">
              <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                User Details - {selectedUser.full_name}
              </DialogTitle>
            </DialogHeader>
          </div>
          
          {/* Sticky Tabs Navigation */}
          <div className="flex-shrink-0 sticky top-0 z-10 bg-slate-900 border-b border-white/10 px-2 overflow-x-auto scrollbar-thin scrollbar-thumb-white/20">
            <div className="flex min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2 whitespace-nowrap transition-colors ${
                    activeTab === tab.id 
                      ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-500/10' 
                      : 'text-white/60 hover:text-white/80 hover:bg-white/5'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable Tab Content */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 min-h-0">
            {/* TAB 1: Overview */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <div className="p-4 bg-white/5 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 bg-purple-600 rounded-full flex items-center justify-center text-xl font-bold">
                      {selectedUser.full_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-lg">{selectedUser.full_name}</p>
                      <p className="text-sm text-white/60">{selectedUser.email}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-white/60 text-xs mb-1">User ID</p>
                      <p className="font-mono text-xs bg-white/5 p-2 rounded truncate">{selectedUser.user_id}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs mb-1">Created</p>
                      <p className="text-sm">{new Date(selectedUser.created_at).toLocaleDateString('en-AU', {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}</p>
                    </div>
                    
                    {/* Account Type - clearer than raw roles */}
                    <div>
                      <p className="text-white/60 text-xs mb-1">Account Type</p>
                      <Badge className={`text-xs ${
                        accountType === 'Platform Owner' ? 'bg-purple-500/30 text-purple-300' :
                        accountType === 'Super Admin' ? 'bg-red-500/30 text-red-300' :
                        accountType === 'Admin' ? 'bg-orange-500/30 text-orange-300' :
                        'bg-white/10 text-white/70'
                      }`}>
                        {accountType}
                      </Badge>
                    </div>

                    {/* Account Status */}
                    <div>
                      <p className="text-white/60 text-xs mb-1">Account Status</p>
                      <Badge data-testid="account-status-badge" className={`text-xs ${
                        selectedUser.is_disabled ? 'bg-red-500/20 text-red-400' :
                        selectedUser.account_status === 'archived' ? 'bg-slate-500/30 text-slate-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {selectedUser.is_disabled ? 'Disabled' :
                         selectedUser.account_status === 'archived' ? 'Archived' : 'Active'}
                      </Badge>
                    </div>
                    
                    {/* Host Status - capability indicator */}
                    <div>
                      <p className="text-white/60 text-xs mb-1">Host Status</p>
                      <Badge className={`text-xs ${
                        hostStatus === 'active' ? 'bg-green-500/20 text-green-400' :
                        hostStatus === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        hostStatus === 'inactive' ? 'bg-red-500/20 text-red-400' :
                        'bg-white/10 text-white/60'
                      }`}>
                        {hostStatus === 'active' ? 'Active' :
                         hostStatus === 'pending' ? 'Pending' :
                         hostStatus === 'inactive' ? 'Inactive' : 'None'}
                      </Badge>
                    </div>
                    
                    {/* Verification Status */}
                    <div className="col-span-2">
                      <p className="text-white/60 text-xs mb-1">Verification Status</p>
                      {isPlatformOwner ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                            Not Required
                          </Badge>
                          <span className="text-[10px] text-white/40">(Platform Owner)</span>
                        </div>
                      ) : (
                        <Badge className={`text-xs ${
                          selectedUser.verification_status === 'verified' ? 'bg-green-500/20 text-green-400' :
                          selectedUser.verification_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          selectedUser.verification_status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                          'bg-white/10 text-white/60'
                        }`}>
                          {selectedUser.verification_status || 'unverified'}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Subscription & Features */}
                  <div className="mt-4 pt-4 border-t border-white/10" data-testid="po-user-entitlements-section">
                    <p className="text-white/60 text-xs mb-3 font-medium flex items-center gap-1.5">
                      <Settings className="w-3.5 h-3.5" />
                      Subscription & Features
                    </p>
                    <div className="space-y-3">
                      {/* Tier Selector */}
                      <div>
                        <p className="text-white/40 text-[11px] mb-1">Tier</p>
                        <select
                          data-testid="po-subscription-tier-select"
                          className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500/50 appearance-none cursor-pointer"
                          value={localEntitlements?.subscription_tier || 'free'}
                          disabled={featureSaving}
                          onChange={(e) => updateUserFeature({ subscription_tier: e.target.value })}
                        >
                          <option value="free" className="bg-slate-800">Free</option>
                          <option value="basic" className="bg-slate-800">Basic</option>
                          <option value="pro" className="bg-slate-800">Pro</option>
                          <option value="premium" className="bg-slate-800">Premium</option>
                        </select>
                      </div>
                      {/* Feature Flags */}
                      <div className="space-y-1">
                        {[
                          { key: 'gps_tracking', label: 'GPS Tracking' },
                          { key: 'priority_support', label: 'Priority Support' },
                          { key: 'boost_listing', label: 'Boost Listing' },
                          { key: 'advanced_analytics', label: 'Advanced Analytics' },
                          { key: 'ai_assistant', label: 'AI Assistant' },
                          { key: 'insurance_perks', label: 'Insurance Perks' }
                        ].map(({ key, label }) => (
                          <label
                            key={key}
                            className="flex items-center justify-between py-1.5 px-1 rounded hover:bg-white/5 cursor-pointer transition-colors"
                            data-testid={`po-feature-flag-${key}`}
                          >
                            <span className="text-sm text-white/80">{label}</span>
                            <div className="relative">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={localEntitlements?.feature_flags?.[key] || false}
                                disabled={featureSaving}
                                onChange={(e) => updateUserFeature({ feature_flags: { [key]: e.target.checked } })}
                              />
                              <div className="w-9 h-5 bg-white/10 rounded-full peer-checked:bg-purple-600 transition-colors" />
                              <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform" />
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Shadow Mode - View As User (Platform Owner Support Tool) */}
                  {!isPlatformOwner && accountType !== 'Super Admin' && accountType !== 'Admin' && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-white/60 text-xs mb-2">Support Tools</p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                          onClick={() => onStartShadowSession && onStartShadowSession(selectedUser.user_id, 'support')}
                          data-testid="view-as-user-btn"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View as User
                        </Button>
                        {hostStatus === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                            onClick={() => onStartShadowSession && onStartShadowSession(selectedUser.user_id, 'host_support')}
                            data-testid="view-as-host-btn"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View as Host
                          </Button>
                        )}
                      </div>
                      <p className="text-[10px] text-white/30 mt-2">
                        Opens a shadow session to view the app as this user. All actions are audited.
                      </p>
                    </div>
                  )}
                  
                  {/* User Lifecycle Actions - Only for non-protected users */}
                  {!isProtectedUser() && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-white/60 text-xs mb-2 font-medium">User Lifecycle</p>
                      <div className="flex flex-wrap gap-2">
                        {/* Disable / Enable toggle */}
                        {!selectedUser.is_disabled && selectedUser.account_status !== 'archived' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 text-xs"
                            onClick={async () => {
                              try {
                                await api.post(`/platform-owner/users/${selectedUser.user_id}/disable/`);
                                toast.success('User disabled');
                                (onRefresh || onClose)();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to disable user'); }
                            }}
                            data-testid="disable-user-btn"
                          >
                            Disable User
                          </Button>
                        )}
                        {selectedUser.is_disabled && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-500/40 text-green-400 hover:bg-green-500/10 text-xs"
                            onClick={async () => {
                              try {
                                await api.post(`/platform-owner/users/${selectedUser.user_id}/enable/`);
                                toast.success('User re-enabled');
                                (onRefresh || onClose)();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to enable user'); }
                            }}
                            data-testid="enable-user-btn"
                          >
                            Re-enable User
                          </Button>
                        )}
                        {/* Archive / Unarchive toggle */}
                        {selectedUser.account_status !== 'archived' && !selectedUser.is_disabled && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-slate-400/40 text-slate-300 hover:bg-slate-500/10 text-xs"
                            onClick={async () => {
                              try {
                                await api.post(`/platform-owner/users/${selectedUser.user_id}/archive/`);
                                toast.success('User archived');
                                (onRefresh || onClose)();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to archive user'); }
                            }}
                            data-testid="archive-user-btn"
                          >
                            Archive User
                          </Button>
                        )}
                        {selectedUser.account_status === 'archived' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10 text-xs"
                            onClick={async () => {
                              try {
                                await api.post(`/platform-owner/users/${selectedUser.user_id}/unarchive/`);
                                toast.success('User restored from archive');
                                (onRefresh || onClose)();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to unarchive user'); }
                            }}
                            data-testid="unarchive-user-btn"
                          >
                            Restore from Archive
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Delete User Section - Only for non-protected users in preview */}
                  {!isProtectedUser() && onDeleteUser && (
                    <div className="mt-4 pt-4 border-t border-red-500/20">
                      <p className="text-red-400/80 text-xs mb-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Danger Zone (Preview Only)
                      </p>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="w-full bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30"
                        onClick={() => {
                          onDeleteUser(selectedUser, 'user');
                          onClose(); // Close drawer to show delete modal
                        }}
                        data-testid="delete-user-drawer-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete User
                      </Button>
                      <p className="text-[10px] text-red-400/50 mt-2">
                        Permanently removes this user and all their data. Cannot be undone.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB 2: Verification & Documents */}
            {activeTab === 'verification' && (
              <div className="space-y-4">
                {isPlatformOwner ? (
                  <div className="p-4 bg-purple-500/10 rounded-xl text-center">
                    <Shield className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                    <p className="text-sm text-purple-300">Verification: Not Required (Platform Owner)</p>
                    <p className="text-xs text-white/40 mt-1">Platform owners bypass identity verification requirements</p>
                  </div>
                ) : selectedVerification ? (
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="font-medium mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-orange-400" />
                      Verification Documents
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {/* Licence Front */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-white/60">Licence Front</p>
                          {getDocStatusBadge('licence_front')}
                        </div>
                        <button 
                          onClick={() => handleOpenDocument('licence_front')}
                          disabled={docLoading.licence_front || docStatus.licence_front === 'not_uploaded'}
                          className="block w-full"
                          data-testid="view-licence-front-btn"
                        >
                          <div className={`aspect-video rounded-lg flex items-center justify-center transition-colors overflow-hidden ${
                            docStatus.licence_front === 'available' ? 'bg-green-500/10 hover:bg-green-500/20 cursor-pointer' :
                            docStatus.licence_front === 'missing' ? 'bg-red-500/10 hover:bg-red-500/20 cursor-pointer' :
                            docStatus.licence_front === 'not_uploaded' ? 'bg-slate-500/10 cursor-not-allowed' :
                            docStatus.licence_front === 'error' ? 'bg-red-500/10' :
                            'bg-white/10 hover:bg-white/20 cursor-pointer'
                          }`}>
                            {docUrls.licence_front ? (
                              <img src={docUrls.licence_front} alt="Licence Front" className="w-full h-full object-cover rounded-lg" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                            ) : null}
                            <div style={{ display: docUrls.licence_front ? 'none' : 'flex' }} className="items-center justify-center w-full h-full">
                              {docLoading.licence_front ? (
                                <RefreshCw className="w-6 h-6 text-white/60 animate-spin" />
                              ) : docStatus.licence_front === 'not_uploaded' ? (
                                <Upload className="w-6 h-6 text-slate-500" />
                              ) : docStatus.licence_front === 'missing' ? (
                                <FileX className="w-6 h-6 text-red-400" />
                              ) : docStatus.licence_front === 'error' ? (
                                <AlertTriangle className="w-6 h-6 text-red-400" />
                              ) : docStatus.licence_front === 'available' ? (
                                <File className="w-6 h-6 text-green-400" />
                              ) : (
                                <RefreshCw className="w-6 h-6 text-white/40 animate-spin" />
                              )}
                            </div>
                          </div>
                        </button>
                      </div>
                      
                      {/* Licence Back */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-white/60">Licence Back</p>
                          {getDocStatusBadge('licence_back')}
                        </div>
                        <button 
                          onClick={() => handleOpenDocument('licence_back')}
                          disabled={docLoading.licence_back || docStatus.licence_back === 'not_uploaded'}
                          className="block w-full"
                          data-testid="view-licence-back-btn"
                        >
                          <div className={`aspect-video rounded-lg flex items-center justify-center transition-colors overflow-hidden ${
                            docStatus.licence_back === 'available' ? 'bg-green-500/10 hover:bg-green-500/20 cursor-pointer' :
                            docStatus.licence_back === 'missing' ? 'bg-red-500/10 hover:bg-red-500/20 cursor-pointer' :
                            docStatus.licence_back === 'not_uploaded' ? 'bg-slate-500/10 cursor-not-allowed' :
                            docStatus.licence_back === 'error' ? 'bg-red-500/10' :
                            'bg-white/10 hover:bg-white/20 cursor-pointer'
                          }`}>
                            {docUrls.licence_back ? (
                              <img src={docUrls.licence_back} alt="Licence Back" className="w-full h-full object-cover rounded-lg" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                            ) : null}
                            <div style={{ display: docUrls.licence_back ? 'none' : 'flex' }} className="items-center justify-center w-full h-full">
                              {docLoading.licence_back ? (
                                <RefreshCw className="w-6 h-6 text-white/60 animate-spin" />
                              ) : docStatus.licence_back === 'not_uploaded' ? (
                                <Upload className="w-6 h-6 text-slate-500" />
                              ) : docStatus.licence_back === 'missing' ? (
                                <FileX className="w-6 h-6 text-red-400" />
                              ) : docStatus.licence_back === 'error' ? (
                                <AlertTriangle className="w-6 h-6 text-red-400" />
                              ) : docStatus.licence_back === 'available' ? (
                                <File className="w-6 h-6 text-green-400" />
                              ) : (
                                <RefreshCw className="w-6 h-6 text-white/40 animate-spin" />
                              )}
                            </div>
                          </div>
                        </button>
                      </div>
                      
                      {/* Selfie */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-white/60">Selfie</p>
                          {getDocStatusBadge('selfie')}
                        </div>
                        <button 
                          onClick={() => handleOpenDocument('selfie')}
                          disabled={docLoading.selfie || docStatus.selfie === 'not_uploaded'}
                          className="block w-full"
                          data-testid="view-selfie-btn"
                        >
                          <div className={`aspect-video rounded-lg flex items-center justify-center transition-colors overflow-hidden ${
                            docStatus.selfie === 'available' ? 'bg-green-500/10 hover:bg-green-500/20 cursor-pointer' :
                            docStatus.selfie === 'missing' ? 'bg-red-500/10 hover:bg-red-500/20 cursor-pointer' :
                            docStatus.selfie === 'not_uploaded' ? 'bg-slate-500/10 cursor-not-allowed' :
                            docStatus.selfie === 'error' ? 'bg-red-500/10' :
                            'bg-white/10 hover:bg-white/20 cursor-pointer'
                          }`}>
                            {docUrls.selfie ? (
                              <img src={docUrls.selfie} alt="Selfie" className="w-full h-full object-cover rounded-lg" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                            ) : null}
                            <div style={{ display: docUrls.selfie ? 'none' : 'flex' }} className="items-center justify-center w-full h-full">
                              {docLoading.selfie ? (
                                <RefreshCw className="w-6 h-6 text-white/60 animate-spin" />
                              ) : docStatus.selfie === 'not_uploaded' ? (
                                <Upload className="w-6 h-6 text-slate-500" />
                              ) : docStatus.selfie === 'missing' ? (
                                <FileX className="w-6 h-6 text-red-400" />
                              ) : docStatus.selfie === 'error' ? (
                                <AlertTriangle className="w-6 h-6 text-red-400" />
                              ) : docStatus.selfie === 'available' ? (
                                <File className="w-6 h-6 text-green-400" />
                              ) : (
                                <RefreshCw className="w-6 h-6 text-white/40 animate-spin" />
                              )}
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Verification Actions */}
                    {selectedVerification.status === 'pending' && (
                      <div className="mt-4 space-y-4">
                        {/* Licence Expiry Date Input - Required for Approval */}
                        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <Label className="text-amber-300 text-sm font-medium flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4" />
                            Licence Expiry Date (Required)
                          </Label>
                          <Input
                            type="date"
                            value={licenceExpiryDate}
                            onChange={(e) => setLicenceExpiryDate(e.target.value)}
                            className="bg-slate-900/50 border-amber-500/50 text-white h-10"
                            data-testid="licence-expiry-input"
                            min={new Date().toISOString().split('T')[0]}
                          />
                          <p className="text-xs text-amber-400/70 mt-1">
                            Enter the expiry date shown on the user's driver licence
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => onApproveVerification(selectedVerification.verification_id, licenceExpiryDate)}
                            className="flex-1 bg-green-600 hover:bg-green-700 h-10 text-sm"
                            disabled={actionLoading || !licenceExpiryDate}
                            data-testid="approve-verification-btn"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {actionLoading ? 'Processing...' : 'Approve'}
                          </Button>
                          <Button
                            variant="destructive"
                            className="flex-1 h-10 text-sm"
                            onClick={onRejectVerification}
                            disabled={actionLoading}
                            data-testid="reject-verification-btn"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                        {!licenceExpiryDate && (
                          <p className="text-xs text-amber-400 text-center">
                            * Enter licence expiry date to enable approval
                          </p>
                        )}
                      </div>
                    )}

                    {selectedVerification.status === 'verified' && (
                      <div className="mt-4 space-y-3">
                        <div className="p-3 bg-green-500/10 rounded-lg flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <span className="text-sm text-green-400">This user is verified</span>
                        </div>
                        
                        {/* Licence Expiry Display & Update */}
                        <div className={`p-3 rounded-lg ${
                          selectedUser?.verification_docs?.licence_expiry_date 
                            ? 'bg-blue-500/10 border border-blue-500/30'
                            : 'bg-amber-500/10 border border-amber-500/30'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <Label className={`text-sm font-medium flex items-center gap-2 ${
                              selectedUser?.verification_docs?.licence_expiry_date ? 'text-blue-300' : 'text-amber-300'
                            }`}>
                              <Calendar className="w-4 h-4" />
                              Licence Expiry Date
                            </Label>
                            {selectedUser?.verification_docs?.licence_expiry_date && (
                              <Badge className="bg-blue-500/20 text-blue-300 text-xs">
                                Stored
                              </Badge>
                            )}
                          </div>
                          
                          {selectedUser?.verification_docs?.licence_expiry_date && !licenceExpiryDate ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <p className="text-white font-mono">
                                  {selectedUser.verification_docs.licence_expiry_date}
                                </p>
                                {(() => {
                                  const expiry = new Date(selectedUser.verification_docs.licence_expiry_date);
                                  const now = new Date();
                                  const daysUntil = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
                                  if (daysUntil < 0) {
                                    return <Badge className="bg-red-500/20 text-red-300">EXPIRED</Badge>;
                                  } else if (daysUntil <= 7) {
                                    return <Badge className="bg-red-500/20 text-red-300">Expires in {daysUntil} days</Badge>;
                                  } else if (daysUntil <= 30) {
                                    return <Badge className="bg-amber-500/20 text-amber-300">Expires in {daysUntil} days</Badge>;
                                  }
                                  return null;
                                })()}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full border-blue-500/30 text-blue-300 hover:bg-blue-500/10 h-8 text-xs"
                                onClick={() => setLicenceExpiryDate(selectedUser.verification_docs.licence_expiry_date)}
                                data-testid="edit-licence-expiry-btn"
                              >
                                <Calendar className="w-3 h-3 mr-1" />
                                Update Expiry Date (e.g. after licence renewal)
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {!selectedUser?.verification_docs?.licence_expiry_date && (
                                <div className="flex items-center gap-2 text-amber-400">
                                  <AlertTriangle className="w-4 h-4" />
                                  <span className="text-xs">Licence expiry not recorded - required for compliance alerts</span>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <Input
                                  type="date"
                                  value={licenceExpiryDate}
                                  onChange={(e) => setLicenceExpiryDate(e.target.value)}
                                  className="flex-1 bg-slate-900/50 border-amber-500/50 text-white h-9 text-sm"
                                  data-testid="licence-expiry-input"
                                  min={new Date().toISOString().split('T')[0]}
                                />
                                <Button
                                  size="sm"
                                  className="bg-amber-600 hover:bg-amber-700 h-9"
                                  disabled={!licenceExpiryDate || licenceExpiryUpdating}
                                  onClick={async () => {
                                    if (!licenceExpiryDate) return;
                                    setLicenceExpiryUpdating(true);
                                    try {
                                      await api.post(`/admin/users/${selectedUser.user_id}/update-licence-expiry`, {
                                        licence_expiry_date: licenceExpiryDate
                                      });
                                      toast.success('Licence expiry date saved successfully');
                                      // Update the local user state directly for immediate UI feedback
                                      if (selectedUser) {
                                        selectedUser.verification_docs = {
                                          ...selectedUser.verification_docs,
                                          licence_expiry_date: licenceExpiryDate
                                        };
                                      }
                                      // Clear input to show saved state
                                      setLicenceExpiryDate('');
                                      // Trigger parent refresh to reload user data
                                      if (window.dispatchEvent) {
                                        window.dispatchEvent(new CustomEvent('user-updated', { detail: { userId: selectedUser.user_id } }));
                                      }
                                    } catch (err) {
                                      toast.error('Failed to save licence expiry');
                                      console.error(err);
                                    } finally {
                                      setLicenceExpiryUpdating(false);
                                    }
                                  }}
                                  data-testid="save-licence-expiry-btn"
                                >
                                  {licenceExpiryUpdating ? 'Saving...' : 'Save'}
                                </Button>
                                {selectedUser?.verification_docs?.licence_expiry_date && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-white/60 hover:text-white h-9"
                                    onClick={() => setLicenceExpiryDate('')}
                                  >
                                    Cancel
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {selectedVerification.status === 'rejected' && (
                      <div className="mt-4 p-3 bg-red-500/10 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <X className="w-5 h-5 text-red-400" />
                          <span className="text-sm text-red-400">Verification rejected</span>
                        </div>
                        {selectedVerification.rejection_reason && (
                          <p className="text-xs text-white/60 ml-7">Reason: {selectedVerification.rejection_reason}</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 bg-white/5 rounded-xl text-center">
                    <Shield className="w-8 h-8 text-white/20 mx-auto mb-2" />
                    <p className="text-sm text-white/60">No verification documents submitted</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB 3: Payout Status */}
            {activeTab === 'stripe' && (
              <div className="space-y-4">
                {stripeLoading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-white/40 mx-auto" />
                    <p className="text-sm text-white/40 mt-2">Loading payout data...</p>
                  </div>
                ) : stripeData?.manual_payout_details ? (
                  /* Manual payout details (HireHub-native) */
                  <div className="p-4 bg-white/5 rounded-xl space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-green-400" />
                        <span className="font-medium">Payout Details</span>
                      </div>
                      <Badge className="bg-green-500/20 text-green-400" data-testid="payout-status-badge">Ready</Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="bg-white/5 p-3 rounded-lg">
                        <p className="text-xs text-white/60 mb-1">Account Name</p>
                        <p className="text-sm font-medium">{stripeData.manual_payout_details.account_name}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white/5 p-3 rounded-lg">
                          <p className="text-xs text-white/60 mb-1">Bank</p>
                          <p className="text-sm">{stripeData.manual_payout_details.bank_name}</p>
                        </div>
                        <div className="bg-white/5 p-3 rounded-lg">
                          <p className="text-xs text-white/60 mb-1">BSB</p>
                          <p className="text-sm font-mono">{stripeData.manual_payout_details.bsb_display}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white/5 p-3 rounded-lg">
                          <p className="text-xs text-white/60 mb-1">Account</p>
                          <p className="text-sm font-mono">{stripeData.manual_payout_details.account_number_masked}</p>
                        </div>
                        <div className="bg-white/5 p-3 rounded-lg">
                          <p className="text-xs text-white/60 mb-1">Method</p>
                          <p className="text-sm">{stripeData.manual_payout_details.preferred_method === 'payid' ? 'PayID' : 'Bank Transfer'}</p>
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-white/40 text-center">Manual payouts only</p>

                    <Button
                      onClick={handleRefreshStripeStatus}
                      disabled={stripeLoading}
                      variant="outline"
                      className="w-full border-white/20 h-10"
                      data-testid="refresh-payout-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${stripeLoading ? 'animate-spin' : ''}`} />
                      Refresh Status
                    </Button>
                  </div>
                ) : stripeData?.stripe_account_id ? (
                  /* Connected account (legacy) */
                  <div className="p-4 bg-white/5 rounded-xl space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-purple-400" />
                        <span className="font-medium">Payout Account</span>
                      </div>
                      <Badge className="bg-green-500/20 text-green-400">Ready for Payouts</Badge>
                    </div>
                    
                    <div className="bg-white/5 p-3 rounded-lg">
                      <p className="text-xs text-white/60 mb-1">Account Reference</p>
                      <p className="font-mono text-sm">{stripeData.stripe_account_id}</p>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white/5 p-3 rounded-lg text-center">
                        <p className="text-xs text-white/60 mb-1">Payments</p>
                        <Badge className={stripeData.charges_enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                          {stripeData.charges_enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div className="bg-white/5 p-3 rounded-lg text-center">
                        <p className="text-xs text-white/60 mb-1">Payouts</p>
                        <Badge className={stripeData.payouts_enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                          {stripeData.payouts_enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div className="bg-white/5 p-3 rounded-lg text-center">
                        <p className="text-xs text-white/60 mb-1">Details</p>
                        <Badge className={stripeData.details_submitted ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                          {stripeData.details_submitted ? 'Submitted' : 'Pending'}
                        </Badge>
                      </div>
                    </div>

                    <Button
                      onClick={handleRefreshStripeStatus}
                      disabled={stripeLoading}
                      variant="outline"
                      className="w-full border-white/20 h-10"
                      data-testid="refresh-payout-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${stripeLoading ? 'animate-spin' : ''}`} />
                      Refresh Status
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-6 bg-white/5 rounded-xl text-center">
                      <Wallet className="w-10 h-10 text-white/20 mx-auto mb-3" />
                      <p className="font-medium text-white/60">No payout details provided</p>
                      <p className="text-xs text-white/40 mt-1">Manual payouts only</p>
                      <p className="text-xs text-white/40">
                        Host Status: {stripeData?.host_status || hostStatus}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TAB 4: Vehicles */}
            {activeTab === 'vehicles' && (
              <div className="space-y-4">
                {vehiclesLoading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-white/40 mx-auto" />
                    <p className="text-sm text-white/40 mt-2">Loading vehicles...</p>
                  </div>
                ) : vehicles.length === 0 ? (
                  <div className="p-8 text-center text-white/40">
                    <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No vehicles listed</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {vehicles.map((vehicle) => (
                      <div 
                        key={vehicle.vehicle_id}
                        className="bg-white/5 rounded-lg p-4 border border-white/10 hover:border-white/20 transition-colors"
                      >
                        <div className="flex gap-4">
                          {/* Vehicle Photo */}
                          <div className="w-24 h-20 bg-white/10 rounded-lg flex-shrink-0 overflow-hidden">
                            {vehicle.photo_url ? (
                              <img 
                                src={vehicle.photo_url} 
                                alt={vehicle.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Car className="w-8 h-8 text-white/30" />
                              </div>
                            )}
                          </div>
                          
                          {/* Vehicle Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-white truncate">{vehicle.title}</h4>
                                <p className="text-xs text-white/50 font-mono">{vehicle.vehicle_id}</p>
                              </div>
                              <Badge 
                                variant={vehicle.status === 'live' ? 'default' : 'secondary'}
                                className={
                                  vehicle.status === 'live' 
                                    ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                    : vehicle.status === 'paused'
                                    ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                    : 'bg-white/10 text-white/60'
                                }
                              >
                                {vehicle.status || 'draft'}
                              </Badge>
                            </div>
                            
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-white/60">
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                {vehicle.daily_rate ? `$${vehicle.daily_rate}/day` : 'Price not set'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Shield className="w-3 h-3" />
                                ${vehicle.bond_amount || 0} bond
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {vehicle.booking_count} booking{vehicle.booking_count !== 1 ? 's' : ''}
                              </span>
                            </div>
                            
                            {vehicle.next_booking && (
                              <div className="mt-2 text-xs text-blue-400 bg-blue-500/10 rounded px-2 py-1 inline-block">
                                Next: {vehicle.next_booking.start_date} → {vehicle.next_booking.end_date}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex justify-end mt-3 pt-3 border-t border-white/10">
                          <a 
                            href={`/vehicles/${vehicle.vehicle_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white">
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Open Listing
                            </Button>
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TAB 5: Bookings */}
            {activeTab === 'bookings' && (
              <div className="space-y-4">
                {bookingsLoading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-white/40 mx-auto" />
                    <p className="text-sm text-white/40 mt-2">Loading bookings...</p>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-white/60 mb-2">
                      {bookings.length} booking(s) for this user
                    </div>
                    {bookings.length > 0 ? (
                      <div className="space-y-2">
                        {bookings.map((booking) => (
                          <div 
                            key={booking.booking_id}
                            className="p-3 bg-white/5 rounded-lg hover:bg-white/10 cursor-pointer transition-colors"
                            onClick={() => openBookingInspector(booking.booking_id)}
                            data-testid={`booking-row-${booking.booking_id}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-mono text-xs truncate flex-1">{booking.booking_id}</span>
                              <Badge className={`ml-2 text-xs ${
                                booking.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                booking.status === 'active' ? 'bg-blue-500/20 text-blue-400' :
                                booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                                'bg-white/10 text-white/60'
                              }`}>
                                {booking.status}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                              <div>
                                <p className="text-white/40">Role</p>
                                <p className="text-white/80">{booking.user_role || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-white/40">Total</p>
                                <p className="text-white/80">${(booking.total_amount_dollars || 0).toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-white/40">Bond</p>
                                <p className="text-white/80">
                                  ${(booking.bond_amount_dollars || 0).toFixed(2)}
                                  {booking.bond_status && (
                                    <span className={`ml-1 text-[10px] ${
                                      booking.bond_status === 'authorized' ? 'text-blue-400' :
                                      booking.bond_status === 'released' ? 'text-green-400' :
                                      booking.bond_status === 'captured' ? 'text-amber-400' :
                                      'text-white/40'
                                    }`}>
                                      ({booking.bond_status})
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div>
                                <p className="text-white/40">Platform Fee</p>
                                <p className="text-white/80">${(booking.platform_fee_dollars || 0).toFixed(2)}</p>
                              </div>
                            </div>
                            {booking.vehicle_title && booking.vehicle_title !== 'N/A' && (
                              <p className="text-xs text-white/40 mt-1 truncate">{booking.vehicle_title}</p>
                            )}
                            <div className="flex items-center justify-end mt-2">
                              <Eye className="w-4 h-4 text-white/40" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 bg-white/5 rounded-xl text-center">
                        <FileText className="w-10 h-10 text-white/20 mx-auto mb-3" />
                        <p className="font-medium text-white/60">0 bookings for this user in this environment</p>
                        <p className="text-xs text-white/40 mt-1">Query: host_id OR customer_id = {selectedUser.user_id}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* TAB 6: Audit Log */}
            {activeTab === 'audit' && (
              <div className="space-y-4">
                {auditLoading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-white/40 mx-auto" />
                    <p className="text-sm text-white/40 mt-2">Loading audit logs...</p>
                  </div>
                ) : auditLogs.length > 0 ? (
                  <div className="space-y-2">
                    {auditLogs.map((log, i) => (
                      <div key={i} className="p-3 bg-white/5 rounded-lg text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={`text-[10px] ${
                            log.action?.includes('APPROVED') || log.action?.includes('verified') ? 'bg-green-500/20 text-green-400' :
                            log.action?.includes('REJECTED') || log.action?.includes('rejected') ? 'bg-red-500/20 text-red-400' :
                            log.action?.includes('stripe') || log.action?.includes('STRIPE') ? 'bg-purple-500/20 text-purple-400' :
                            'bg-white/10 text-white/60'
                          }`}>
                            {log.action}
                          </Badge>
                          <span className="text-white/40">
                            {new Date(log.created_at).toLocaleString('en-AU')}
                          </span>
                        </div>
                        {log.details && (
                          <p className="text-white/60 mt-1 truncate">
                            {typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 bg-white/5 rounded-xl text-center">
                    <Clock className="w-10 h-10 text-white/20 mx-auto mb-3" />
                    <p className="font-medium text-white/60">No Audit Logs</p>
                    <p className="text-xs text-white/40 mt-1">No activity recorded for this user</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB 7: Contact */}
            {activeTab === 'contact' && (
              <div className="space-y-4">
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="font-medium mb-4 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-400" />
                    Contact Information
                  </p>
                  
                  {/* Full Name */}
                  <div className="mb-4">
                    <p className="text-xs text-white/60 mb-1">Name</p>
                    <p className="text-lg font-medium">{selectedUser.full_name || 'No name on file'}</p>
                  </div>
                  
                  {/* Email */}
                  <div className="mb-4">
                    <p className="text-xs text-white/60 mb-1">Email</p>
                    <div className="flex items-center gap-2">
                      <p className="text-base flex-1 truncate">{selectedUser.email || 'No email on file'}</p>
                      {selectedUser.email && (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(selectedUser.email);
                            toast.success('Email copied to clipboard');
                          }}
                          className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                          title="Copy Email"
                          data-testid="copy-email-btn"
                        >
                          <Copy className="w-4 h-4 text-white/60" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Phone */}
                  <div className="mb-6">
                    <p className="text-xs text-white/60 mb-1">Phone</p>
                    {selectedUser.phone ? (
                      <div className="flex items-center gap-2">
                        <p className="text-base flex-1">{selectedUser.phone}</p>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(selectedUser.phone);
                            toast.success('Phone copied to clipboard');
                          }}
                          className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                          title="Copy Phone"
                          data-testid="copy-phone-btn"
                        >
                          <Copy className="w-4 h-4 text-white/60" />
                        </button>
                      </div>
                    ) : (
                      <p className="text-white/40 italic">No phone on file</p>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="space-y-3">
                    {/* Call Now Button */}
                    {selectedUser.phone ? (
                      <a 
                        href={`tel:${selectedUser.phone}`}
                        className="block w-full"
                        data-testid="call-now-btn"
                      >
                        <Button className="w-full bg-green-600 hover:bg-green-700 h-12 text-base font-medium">
                          <Phone className="w-5 h-5 mr-2" />
                          Call Now
                        </Button>
                      </a>
                    ) : (
                      <Button 
                        disabled 
                        className="w-full bg-white/10 h-12 text-base font-medium cursor-not-allowed opacity-50"
                      >
                        <Phone className="w-5 h-5 mr-2" />
                        Call Now (No phone)
                      </Button>
                    )}
                    
                    {/* Send Email Button */}
                    {selectedUser.email ? (
                      <a 
                        href={`mailto:${selectedUser.email}`}
                        className="block w-full"
                        data-testid="send-email-btn"
                      >
                        <Button variant="outline" className="w-full border-white/20 h-12 text-base font-medium hover:bg-white/10">
                          <Mail className="w-5 h-5 mr-2" />
                          Send Email
                        </Button>
                      </a>
                    ) : (
                      <Button 
                        disabled 
                        variant="outline" 
                        className="w-full border-white/10 h-12 text-base font-medium cursor-not-allowed opacity-50"
                      >
                        <Mail className="w-5 h-5 mr-2" />
                        Send Email (No email)
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Urgent Contact Note */}
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-xs text-amber-300 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    For urgent incidents (vehicle safety, overdue returns), use Call Now to reach the user immediately.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Fixed Footer */}
          <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-t border-white/10 bg-slate-900">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="border-white/20 w-full sm:w-auto h-10"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Inspector Modal */}
      <Dialog open={showBookingInspector} onOpenChange={setShowBookingInspector}>
        <DialogContent className="bg-slate-900 border-white/20 text-white mx-4 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Booking Inspector
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="bg-white/5 p-4 rounded-lg space-y-3">
                <div>
                  <p className="text-xs text-white/60 mb-1">Booking ID</p>
                  <p className="font-mono text-sm">{selectedBooking.booking_id}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-white/60 mb-1">Payment Intent ID</p>
                    <p className="font-mono text-xs truncate">{selectedBooking.payment_intent_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 mb-1">Charge ID</p>
                    <p className="font-mono text-xs truncate">{selectedBooking.charge_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 mb-1">Transfer ID</p>
                    <p className="font-mono text-xs truncate">{selectedBooking.transfer_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 mb-1">Payout ID</p>
                    <p className="font-mono text-xs truncate">{selectedBooking.payout_id || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowBookingInspector(false)}
              className="border-white/20 w-full sm:w-auto h-10"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserDetailsDrawer;
